import { useCallback, useState } from 'react';

import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import { ModalAuthScreen, ModalAuthState } from '../types';

export interface ModalAuthHook {
  modalAuthState: ModalAuthState;
  setModalAuthState: (modalAuthstate: ModalAuthState) => void;
  modalAuthIsOpen: boolean;
}

// This hook is used to track the modal auth state. Which is used to determine if the user is in the modal auth state or not.
export const useModalAuth = (): ModalAuthHook => {
  const { logRBIEvent } = useMParticleContext();

  const [modalAuthState, setModalAuthState] = useState<ModalAuthState>({
    screen: ModalAuthScreen.CLOSED,
  });

  const modalAuthIsOpen = modalAuthState.screen !== ModalAuthScreen.CLOSED;

  /**
   * A handler function to update the modal authentication state.
   * It merges the new state with the previous state, ensuring that no data is lost
   * and only specified properties are updated.
   * If the modal auth screen is set to 'closed', all states are reset to their default values.
   *
   * @param {Partial<ModalAuthState>} newState - The new state to be merged with the previous state.
   */
  const handleSetModalAuthState = useCallback(
    ({ screen, errorMessage, user, loginForVendorIsLoading }: Partial<ModalAuthState>) => {
      setModalAuthState(prevModalAuthState => {
        if (screen && prevModalAuthState.screen !== screen) {
          logRBIEvent({
            name: CustomEventNames.AUTH_DRAWER_VIEW,
            type: EventTypes.Other,
            attributes: {
              screen,
            },
          });
        }

        if (screen === ModalAuthScreen.CLOSED) {
          return {
            screen,
          };
        }

        const {
          screen: prevScreen,
          errorMessage: prevErrorMessage,
          user: prevUser,
          loginForVendorIsLoading: prevLoginForVendorIsLoading,
        } = prevModalAuthState;

        const updatedUser = user
          ? {
              ...prevUser,
              name: user.name ?? prevUser?.name,
              email: user.email ?? prevUser?.email,
            }
          : prevUser;

        return {
          screen: screen ?? prevScreen,
          errorMessage: errorMessage ?? prevErrorMessage,
          user: updatedUser,
          loginForVendorIsLoading: loginForVendorIsLoading ?? prevLoginForVendorIsLoading,
        };
      });
    },
    [logRBIEvent]
  );

  return { modalAuthState, setModalAuthState: handleSetModalAuthState, modalAuthIsOpen };
};
